import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { TasksContainer } from '@/containers/tasks/tasks-container.component';
import {
  decodeTaskFilters,
  encodeTaskFilters,
  TASK_VIEW_KEYS,
  TASKS_URL_KEYS,
  type TasksUrlState,
} from '@/containers/tasks/task-url-state';
import { SequencingPage } from '@/containers/sequences/sequencing-page.component';
import { useSequencingNavigation } from '@/containers/sequences/use-sequencing-navigation.hook';

// The page is its address: the view, the filters and the open task live in
// the search, so a refresh or Back lands where the rep was.
// `task` opens one task, whichever list it is in (it is also the link a
// mirrored CRM task carries back into Cleanlist). `view=review` opens the
// Review category: the Clu drafts waiting for a member.
const text = z.string().max(200).optional().catch(undefined);
const tasksSearchSchema = z.object({
  task: z.string().optional(),
  view: z
    .enum(TASK_VIEW_KEYS as [string, ...string[]])
    .optional()
    .catch(undefined),
  q: text,
  due: text,
  sequence: text,
  owner: text,
  priority: text,
  type: text,
  state: text,
  outcome: text,
  completed: text,
});

export const Route = createFileRoute('/tasks')({
  component: TasksRoute,
  validateSearch: tasksSearchSchema,
});

function TasksRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const onNavigate = useSequencingNavigation();
  const urlState = search as TasksUrlState;
  return (
    <SequencingPage title='Tasks'>
      <TasksContainer
        initialFilters={decodeTaskFilters(urlState)}
        onFiltersChange={(filters) => {
          const next = encodeTaskFilters(filters);
          if (TASKS_URL_KEYS.every((key) => next[key] === urlState[key]))
            return;
          void navigate({
            to: '/tasks',
            search: (previous) => ({
              ...previous,
              ...Object.fromEntries(
                TASKS_URL_KEYS.map((key) => [key, undefined])
              ),
              ...next,
            }),
            replace: true,
          });
        }}
        onNavigate={onNavigate}
        linkedTaskId={search.task}
        onTaskChange={(id, replace = false) =>
          void navigate({
            to: '/tasks',
            search: (previous) => ({ ...previous, task: id ?? undefined }),
            replace,
          })
        }
        onLinkedTaskClose={() =>
          void navigate({
            to: '/tasks',
            search: (previous) => ({ ...previous, task: undefined }),
            replace: true,
          })
        }
      />
    </SequencingPage>
  );
}
